
<template>
  <div
    v-if="!isMobile"
    class="faq-switcher">
    <div class="faq-switcher-categories">
      <button
        v-for="category in categoryKeys"
        :key="category"
        class="faq-category"
        :class="{ 'selected': selectedCategorySwitcher === category }"
        @click="setSelectedCategorySwitcher(category)">
        {{ faq[category].title }}
      </button>
    </div>
    <Transition
      mode="out-in"
      :name="`slide-${transitionDirection}`">
      <div
        :key="selectedCategorySwitcher"
        class="faq-switcher-content">
        <div
          v-for="(qAndA, index) in selectedCategorySwitcherData"
          :key="index"
          class="faq">
          <p class="question">
            {{ qAndA.question }}
          </p>
          <p
            class="answer"
            v-html="qAndA.answer"/>
        </div>
      </div>
    </Transition>
  </div>
  <div
    v-else
    class="faq-dropdowns">
    <div class="faq-dropdown-categories">
      <div
        v-for="category in categoryKeys"
        :key="category">
        <button
          :id="category"
          class="faq-dropdown-button"
          :class="{ 'open': openCategoryDropdown === category }"
          @click="setOpenCategoryDropdown(category)">
          {{ faq[category].title }}
          <div class="icon-container">
            <svg-chevron-down
              class="chevron"
              :class="{ 'up': openCategoryDropdown === category }"/>
          </div>
        </button>
        <div
          class="faq-dropdown-content"
          :class="{ 'visible': openCategoryDropdown === category }">
          <div
            v-for="(qAndA, index) in faq[category].data"
            :key="index"
            class="faq">
            <p class="question">
              {{ qAndA.question }}
            </p>
            <p
              class="answer"
              v-html="qAndA.answer"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useFaq from '@/homepage/composables/faq.js'
import SvgChevronDown from '@/homepage/components/svg/SvgChevronDown.vue'
import useScreenSize from '@shared/composables/screenSize.js'

export default {
  components: {
    SvgChevronDown
  },
  setup () {
    const { faq } = useFaq()
    const { isMobile } = useScreenSize()
    return {
      faq,
      isMobile,
    }
  },
  data () {
    const currentCategory = this.$route.query.category ?? 'basics'
    return {
      prevCategorySwitcher: currentCategory,
      selectedCategorySwitcher: currentCategory,
      openCategoryDropdown: currentCategory
    }
  },
  computed: {
    categoryKeys () {
      return Object.keys(this.faq)
    },
    selectedCategorySwitcherData () {
      return this.faq[this.selectedCategorySwitcher].data
    },
    transitionDirection () {
      return this.categoryKeys.indexOf(this.prevCategorySwitcher) < this.categoryKeys.indexOf(this.selectedCategorySwitcher) ? 'left' : 'right'
    }
  },
  watch: {
    selectedCategorySwitcher: {
      handler (category) {
        this.$router.push({ query: { category } })
      },
      immediate: true
    },
    openCategoryDropdown: {
      handler (category) {
        const query = category ? { category } : {}
        this.$router.push({ query })
      },
      immediate: true
    },
    isMobile: {
      handler (isMobile) {
        if (isMobile) {
          this.openCategoryDropdown = this.selectedCategorySwitcher
        } else {
          this.selectedCategorySwitcher = this.openCategoryDropdown ?? this.categoryKeys[0]
        }
      }
    },
    '$route.query.category': {
      handler (category) {
        // This watcher is still active on other routes due to KeepAlive
        // so we need to make sure we're on the correct page first.
        if (this.$route.name !== 'frequently-asked-questions') {
          return
        }
        if (!this.categoryKeys.includes(category)) {
          if (category || !this.isMobile) {
            category = this.categoryKeys[0]
          }
        }
        if (this.isMobile) {
          this.openCategoryDropdown = category
        } else {
          this.selectedCategorySwitcher = category
        }
      }
    }
  },
  activated () {
    this.$router.replace({ query: { category: this.selectedCategorySwitcher } })
  },
  methods: {
    setOpenCategoryDropdown (categoryKey) {
      if (categoryKey === this.openCategoryDropdown) {
        this.openCategoryDropdown = null
      } else {
        this.openCategoryDropdown = categoryKey
        // Situates the open faq category at the top of the page on mobile
        this.$nextTick(() => {
          const element = document.getElementById(categoryKey)
          // Without the timeout, the scrollIntoView method doesn't work.
          setTimeout(() => element.scrollIntoView(), 0)
        })
      }
    },
    setSelectedCategorySwitcher (categoryKey) {
      this.prevCategorySwitcher = this.selectedCategorySwitcher
      this.selectedCategorySwitcher = categoryKey
    }
  }
}
</script>

<style lang="scss" scoped>
.faq-detailed {
  background: $secondary;
  padding: 36px 12px;

  @include medium {
    padding: 64px 0;
  }

  .section-header {
    margin-top: 0;
    padding: 0 16px;
  }
}

.faq-dropdowns {
  display: flex;
  flex-direction: column;
  gap: $spacing-xl;

  .faq-dropdown-categories {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
  }

  .faq-dropdown-button {
    width: 100%;
    border: 1px solid $primary;
    border-radius: $system-border-radius;
    font-family: $headings-font-family;
    font-size: $body-font-size;
    font-weight: $font-weight-bold;
    text-decoration: none;
    text-align: left;
    cursor: pointer;
    color: $plum;
    background-color: $origami;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    scroll-margin-top: 100px;

    @include anchor-link($spacing-lg);

    &:focus {
      outline: none;
    }

    &.open, &:active {
      background-color: $plum;
      color: $white;
    }

    .button-text {
      width: 100%;
    }

    .icon-container {
      align-self: center;

      .chevron {
        width: 14px;
        height: 8px;
        @include transition-base-all;

        &.up {
          color: white;
          transform: rotate(180deg);
          -webkit-transform: rotate(180deg);
        }
      }
    }
  }

.faq-dropdown-content {
  display: flex;
  flex-direction: column;
  gap: $spacing-xl;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
  opacity: 0;

  &.visible {
    max-height: 3000px;
    opacity: 1;
    transition: max-height 0.5s ease-in, opacity 0.3s ease-in;
  }

  .faq {
    margin: unset;
    width: 100%;
    &:first-child {
      margin-top: $spacing-lg;
    }
  }
}
}

.faq-switcher {
  display: block;

  .faq-switcher-categories {
    max-width: 772px;
    width: 100%;
    margin: 0 auto $spacing-xxl auto;
    display: flex;
    justify-content: space-between;
    border: 1px solid $primary;
    border-radius: $system-border-radius;
    border-right: none;

    .faq-category {
      width: 20%;
      height: 50px;
      padding: 0 $spacing-xs;
      font-family: $headings-font-family;
      font-weight: $font-weight-bold;
      border: none;
      color: $plum;
      background-color: $origami;
      cursor: pointer;
      border-right: 1px solid $plum;

      &:focus {
        outline: none;
      }

      &.selected {
        background-color: $plum;
        color: $white;
      }
    }
  }

  .faq-switcher-content {
    width: 100%;
    max-width: 920px;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: $spacing-lg;

    .faq {
      max-width: 400px;
      width: calc((100% - #{$spacing-lg}) / 2);
    }
  }
}

.faq {
  width: 400px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;

  .question {
    font-weight: $font-weight-bold;
    margin: 0;
  }

  :deep(a) {
    text-decoration: underline;
    text-decoration-thickness: 1px;
  }
}

// Animations
.fade-enter-active,
.fade-leave,
.fade-leave-active,
.slide-left-leave-active,
.slide-right-leave-active {
  transition: all 0.25s ease-in-out;
}

.slide-left-enter-active,
.slide-right-enter-active {
  transition: all 0.25s ease-out;
}

.slide-left-leave-to,
.slide-right-enter {
  transform: translateX(-5vw);
}

.slide-left-enter,
.slide-right-leave-to {
  transform: translateX(5vw);
}

.fade-enter,
.fade-leave-to,
.slide-left-leave-to,
.slide-right-enter,
.slide-left-enter,
.slide-right-leave-to {
  opacity: 0;
}
</style>
